/**
 * TRHC "Bold Ranch" (D) storefront behaviour: hero slideshow, bottom-sheet dialogs (fit guide, gift tiers),
 * rating scroll, and "Often bought together" add-all. Loaded as a module by the trhc-d sections and blocks.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const reduceMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const money = (cents) => '$' + (cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2));

/* Re-render the cart drawer from the server (Horizon doesn't always refresh it after an add from our code,
   or after the cart changed in another tab/page), then optionally open it. */
async function refreshDrawer(open) {
  try {
    const m = await import('@theme/section-renderer');
    await m.sectionRenderer.renderSection('cart-drawer-section', { cache: false });
  } catch (e) {
    console.warn('[trhc] drawer refresh', e);
  }
  if (open) {
    const td = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
    if (td && !td.isOpen) td.open?.();
  }
}
let tdCartCount = null;
async function syncDrawerIfStale() {
  try {
    const c = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    if (tdCartCount !== null && c.item_count !== tdCartCount) await refreshDrawer(false);
    tdCartCount = c.item_count;
  } catch (e) {}
}
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') syncDrawerIfStale();
});
window.addEventListener('pageshow', (e) => {
  if (e.persisted) refreshDrawer(false);
});
syncDrawerIfStale();

/* ---------- Hero slideshow ---------- */
class TdHero extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.td-hero__track');
    this.dots = [...this.querySelectorAll('.td-hero__dot')];
    if (!this.track || this.dots.length < 2) return;
    this.index = 0;
    this.track.addEventListener('scroll', () => this.#sync(), { passive: true });
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));
    this.querySelector('[data-td-hero-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
    this.querySelector('[data-td-hero-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
    ['pointerdown', 'touchstart', 'mouseenter', 'focusin'].forEach((e) =>
      this.addEventListener(e, () => this.#stop(), { passive: true })
    );
    this.addEventListener('mouseleave', () => this.#start());
    this.#start();
  }
  disconnectedCallback() {
    this.#stop();
  }
  go(i, user) {
    const n = this.dots.length;
    this.index = (i + n) % n;
    this.track.scrollTo({ left: this.index * this.track.clientWidth, behavior: reduceMotion() ? 'auto' : 'smooth' });
    if (user) this.#stop();
  }
  #sync() {
    const i = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
    this.index = i;
    this.dots.forEach((d, k) => d.setAttribute('aria-current', k === i ? 'true' : 'false'));
  }
  #start() {
    const ms = Number(this.dataset.interval || 0) * 1000;
    if (!ms || reduceMotion() || this.timer) return;
    this.timer = setInterval(() => this.go(this.index + 1, false), ms);
  }
  #stop() {
    clearInterval(this.timer);
    this.timer = null;
  }
}
if (!customElements.get('td-hero')) customElements.define('td-hero', TdHero);

/* ---------- Dialogs + small actions ---------- */
document.addEventListener('click', (event) => {
  const t = event.target instanceof Element ? event.target : null;
  if (!t) return;
  const opener = t.closest('[data-td-open]');
  if (opener) {
    const dlg = document.getElementById(opener.dataset.tdOpen);
    if (dlg && dlg.showModal) {
      event.preventDefault();
      dlg.showModal();
    }
    return;
  }
  if (t.closest('[data-td-close]')) {
    t.closest('dialog')?.close();
    return;
  }
  if (t instanceof HTMLDialogElement && t.classList.contains('td-dialog')) {
    t.close(); // click on the backdrop
    return;
  }
  const rating = t.closest('[data-td-reviews]');
  if (rating) {
    const target = document.querySelector('#judgeme_product_reviews, .jdgm-review-widget, .jdgm-widget');
    if (target) {
      event.preventDefault();
      target.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
    }
  }
});

/* ---------- Often bought together ---------- */
async function addLines(source, items) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      lines: items.map((i) => ({ merchandiseId: i.id, quantity: i.quantity })),
      context: 'product',
      promise: deferred.promise,
    })
  );
  try {
    const res = await fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: 'cart-drawer-section' }),
    });
    const json = await res.json();
    if (!res.ok || json.status) throw new Error(json.description || json.message || 'Add failed');
    const cart = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections: json.sections, items: cart.items, itemCount: cart.item_count, source: 'trhc-fbt', didError: false },
    });
    tdCartCount = cart.item_count;
    const inModal = source instanceof Element && source.closest('dialog:modal');
    if (!inModal) refreshDrawer(true);
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/* One-tap add (Black Friday cart nudge and anywhere else: [data-td-quickadd][data-variant-id]). */
document.addEventListener('click', async (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-td-quickadd]') : null;
  if (!btn || btn.classList.contains('is-loading')) return;
  event.preventDefault();
  btn.classList.add('is-loading');
  try {
    await addLines(btn, [{ id: Number(btn.dataset.variantId), quantity: 1 }]);
  } catch (e) {
    console.warn('[trhc-quickadd]', e);
    btn.textContent = 'Sold out';
  } finally {
    btn.classList.remove('is-loading');
  }
});

class TdFbt extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector('[data-fbt-add]');
    this.addEventListener('change', (e) => {
      const sel = e.target instanceof HTMLSelectElement ? e.target : null;
      if (sel) this.#swap(sel);
      this.update();
    });
    this.btn?.addEventListener('click', () => this.#add());
    // Keep the main item in step with the product page's variant picker.
    document.addEventListener('change', () => setTimeout(() => this.#syncMain(), 50));
    this.#syncMain();
  }
  #mainInput() {
    return document.querySelector('form[action*="/cart/add"] input[name="id"]');
  }
  #syncMain() {
    const main = this.querySelector('[data-fbt-main]');
    const input = this.#mainInput();
    if (main && input && input.value) main.dataset.variantId = input.value;
    this.update();
  }
  #swap(select) {
    const row = select.closest('[data-fbt-item]');
    const opt = select.selectedOptions[0];
    if (!row || !opt) return;
    row.dataset.variantId = opt.value;
    row.dataset.price = opt.dataset.price;
    const img = row.querySelector('img');
    if (img && opt.dataset.img) img.src = opt.dataset.img;
    const price = row.querySelector('.td-fbt__price');
    if (price) price.textContent = money(Number(opt.dataset.price));
  }
  #checked() {
    return [...this.querySelectorAll('[data-fbt-item]')].filter((r) => r.querySelector('input[type="checkbox"]')?.checked);
  }
  update() {
    const rows = this.#checked();
    const total = rows.reduce((s, r) => s + Number(r.dataset.price || 0), 0);
    const n = rows.length;
    if (this.btn) {
      this.btn.disabled = n === 0;
      this.btn.textContent = n > 1 ? `Add all ${n} · ${money(total)}` : n === 1 ? `Add to cart · ${money(total)}` : 'Pick at least one';
    }
    const note = this.querySelector('[data-fbt-unlock]');
    if (note) {
      const t1 = Number(this.dataset.t1 || 0), t2 = Number(this.dataset.t2 || 0);
      let text = '';
      if (t2 && total >= t2) text = `Unlocks FREE shipping, a FREE ${this.dataset.t1name || 'gift'} and a FREE ${this.dataset.t2name || 'gift'}.`;
      else if (t1 && total >= t1) text = `Unlocks FREE shipping and a FREE ${this.dataset.t1name || 'gift'}.`;
      else if (t1) text = `Add ${money(t1 - total)} more for FREE shipping and a FREE gift.`;
      note.textContent = text;
    }
  }
  async #add() {
    const items = this.#checked()
      .map((r) => ({ id: Number(r.dataset.variantId), quantity: 1 }))
      .filter((i) => i.id);
    if (!items.length) return;
    this.btn.classList.add('is-loading');
    try {
      await addLines(this, items);
    } catch (e) {
      console.warn('[trhc-fbt]', e);
      const note = this.querySelector('[data-fbt-unlock]');
      if (note) note.textContent = 'One of these just sold out. Uncheck it and try again.';
    } finally {
      this.btn.classList.remove('is-loading');
    }
  }
}
if (!customElements.get('td-fbt')) customElements.define('td-fbt', TdFbt);

/* ---------- PDP: single hat vs bundle chooser ---------- */
document.addEventListener('change', (event) => {
  const input = event.target instanceof HTMLInputElement ? event.target : null;
  const offer = input?.closest('[data-td-offer]');
  if (!offer) return;
  offer.querySelectorAll('.td-opt').forEach((opt) => opt.classList.toggle('is-on', opt.contains(input) && input.checked));
  document.documentElement.classList.toggle('td-bundle-on', input.value === 'bundle' && input.checked);
});

/* ---------- PDP: price on the Add to cart button ---------- */
function stampButtonPrice() {
  document.querySelectorAll('.buy-buttons-block').forEach((buy) => {
    const label = buy.querySelector('.add-to-cart-button');
    if (!label) return;
    let scope = buy.parentElement;
    let priceEl = null;
    while (scope && !priceEl) {
      priceEl = scope.querySelector('.price bdi') || scope.querySelector('.price');
      scope = scope.parentElement;
    }
    const raw = priceEl ? priceEl.textContent.trim() : '';
    const price = label.disabled ? '' : raw.replace(/\.00(?!\d)/, '');
    if (price) {
      if (label.dataset.tdPrice !== price) label.dataset.tdPrice = price;
    } else if (label.dataset.tdPrice) {
      delete label.dataset.tdPrice;
    }
  });
}
let stampQueued = false;
const queueStamp = () => {
  if (stampQueued) return;
  stampQueued = true;
  requestAnimationFrame(() => {
    stampQueued = false;
    stampButtonPrice();
  });
};
const buyRoot = document.querySelector('.buy-buttons-block');
if (buyRoot) {
  stampButtonPrice();
  new MutationObserver(queueStamp).observe(buyRoot.closest('.shopify-section') || document.body, { childList: true, subtree: true, characterData: true });
}

/* ---------- PDP: gift progress bar follows the cart ---------- */
async function refreshMeters() {
  const meters = document.querySelectorAll('[data-td-meter]');
  if (!meters.length) return;
  let cart;
  try {
    cart = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  } catch (e) {
    return;
  }
  const q = cart.items.reduce((sum, item) => (item.properties && item.properties._gift_tier ? sum : sum + item.final_line_price), 0);
  // Black Friday (Buy 2 Get 1) meters need the hat count, which cart.js can't tell (no product tags),
  // so they read it from the bf-cart-bar section (only exists in the Black Friday theme).
  let bf = null;
  if (document.querySelector('[data-td-meter][data-mode="b2g1"]')) {
    try {
      const j = await fetch(ROOT + '?sections=bf-cart-bar', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const st = new DOMParser().parseFromString(j['bf-cart-bar'] || '', 'text/html').querySelector('[data-bf-state]');
      if (st) bf = JSON.parse(st.textContent || '{}');
    } catch (e) {}
  }
  meters.forEach((m) => {
    if (m.dataset.mode === 'b2g1') {
      if (!bf) return;
      const thr = Number(m.dataset.thr || 10000), unit = Number(m.dataset.unit || 3500);
      const hats = Number(bf.hats || 0), tot = Number(bf.total || 0);
      const r = hats % 3, got = tot >= thr;
      const perk = '<b>FREE shipping</b> + a <b>FREE mystery hat</b>';
      let t;
      if (r === 2) t = '🎁 <b>This hat is FREE.</b> Add it and it comes off at checkout.';
      else if (r === 1 && got) t = `✓ ${perk} unlocked. Add <b>this hat + 1 more</b> and the next one is <b>FREE</b>.`;
      else if (r === 1) t = 'Add <b>this hat + 1 more</b> and the 3rd is <b>FREE</b>.';
      else if (hats === 0) t = '<b>Buy 2, Get 1 FREE.</b> Add this hat + 2 more and the 3rd is FREE.';
      else if (got) t = `✓ ${perk} unlocked. Add <b>this hat + 2 more</b> for another FREE hat.`;
      else {
        const need = Math.ceil((thr - tot) / unit);
        t = `✓ <b>FREE hat unlocked.</b> Add ${need <= 1 ? '<b>this hat</b>' : `<b>${need} more hats</b>`} for ${perk}.`;
      }
      const bar = m.querySelector('.td-meter__bar i');
      const p = m.querySelector('[data-td-meter-text]');
      if (bar) bar.style.width = Math.max(4, Math.min(100, (tot / thr) * 100)) + '%';
      if (p && p.innerHTML !== t) p.innerHTML = t;
      return;
    }
    const t1 = Number(m.dataset.t1 || 0), t2 = Number(m.dataset.t2 || 0), unit = Number(m.dataset.unit || 0);
    const n1 = m.dataset.t1name || 'gift', n2 = m.dataset.t2name || 'gift';
    // Count only what's already in the cart; the hat on this page is "this hat", not already added.
    let text, pct = 100;
    const needFor = (t) => (unit > 0 ? Math.ceil((t - q) / unit) : 1);
    if (t1 > 0 && q < t1) {
      const need = needFor(t1);
      pct = (q / t1) * 100;
      const gift = `<b>FREE ${n1}</b> + <b>FREE shipping</b>`;
      text = need <= 1 ? `Add <b>this hat</b> to unlock a ${gift}` : q === 0 ? `Add <b>${need} hats</b> for a ${gift}` : `Add <b>${need} more hats</b> for a ${gift}`;
    } else if (t2 > 0 && q < t2) {
      const need = needFor(t2);
      pct = (q / t2) * 100;
      text = `<b>FREE ${n1}</b> + <b>FREE shipping</b> unlocked. ` + (need <= 1 ? `Add <b>this hat</b> for a <b>FREE ${n2}</b>` : `Add <b>${need} more hats</b> for a <b>FREE ${n2}</b>`);
    } else {
      text = `You've unlocked <b>FREE shipping</b>, a <b>FREE ${n1}</b>${t2 > 0 ? ` and a <b>FREE ${n2}</b>` : ''}`;
    }
    const bar = m.querySelector('.td-meter__bar i');
    const p = m.querySelector('[data-td-meter-text]');
    if (bar) bar.style.width = Math.max(4, Math.min(100, pct)) + '%';
    if (p && p.innerHTML !== text) p.innerHTML = text;
  });
}
let meterTimer;
const queueMeter = () => {
  clearTimeout(meterTimer);
  meterTimer = setTimeout(refreshMeters, 350);
};
const drawer = document.querySelector('cart-drawer-component') || document.querySelector('#CartDrawer, .cart-drawer');
if (drawer && document.querySelector('[data-td-meter]')) {
  new MutationObserver(queueMeter).observe(drawer, { childList: true, subtree: true });
}
window.addEventListener('pageshow', (e) => {
  if (e.persisted) queueMeter();
});

/* ---------- PDP: in-page bundle builder (our UI, Easy Bundle's pricing engine) ---------- */
const BB_HATS = 3;
const BB_GIFTS = 2;
const bbState = new WeakMap();

function bbInit(dlg) {
  if (bbState.has(dlg)) return bbState.get(dlg);
  const st = { hats: new Map(), gifts: new Map(), step: 'hats' };
  bbState.set(dlg, st);
  const pre = dlg.querySelector(`[data-bb-hat][data-v="${dlg.dataset.preselect}"]`);
  if (pre) {
    st.hats.set(pre.dataset.v, 1);
    pre.parentElement.prepend(pre); // current hat first
  }
  dlg.addEventListener('click', (e) => bbClick(dlg, st, e));
  bbRender(dlg, st);
  return st;
}

const bbHatCount = (st) => [...st.hats.values()].reduce((a, b) => a + b, 0);

function bbStep(dlg, st, step) {
  st.step = step;
  dlg.querySelectorAll('[data-bb-step]').forEach((b) => b.classList.toggle('is-on', b.dataset.bbStep === step));
  dlg.querySelectorAll('[data-bb-panel]').forEach((p) => (p.hidden = p.dataset.bbPanel !== step));
  dlg.querySelector('.td-bb__inner')?.scrollTo({ top: 0 });
}

function bbRender(dlg, st) {
  const hats = bbHatCount(st);
  const gifts = st.gifts.size;
  dlg.querySelectorAll('[data-bb-hat]').forEach((card) => {
    const n = st.hats.get(card.dataset.v) || 0;
    card.classList.toggle('is-on', n > 0);
    card.querySelector('[data-bb-add]').hidden = n > 0;
    card.querySelector('.td-bb__step').hidden = n === 0;
    card.querySelector('[data-bb-n]').textContent = n;
    const badge = card.querySelector('[data-bb-qty]');
    badge.hidden = n === 0;
    badge.textContent = n > 1 ? `×${n}` : '✓';
    card.querySelector('[data-bb-add]').disabled = hats >= BB_HATS;
    card.querySelector('[data-bb-inc]').disabled = hats >= BB_HATS;
  });
  dlg.querySelectorAll('[data-bb-gift]').forEach((card) => {
    const on = st.gifts.has(card.dataset.v);
    card.classList.toggle('is-on', on);
    card.querySelector('.td-bb__badge').hidden = !on;
    const sameProduct = [...st.gifts.values()].includes(card.dataset.p);
    card.classList.toggle('is-off', !on && (gifts >= BB_GIFTS || sameProduct));
  });
  dlg.querySelector('[data-bb-count="hats"]').textContent = `${hats}/${BB_HATS}`;
  dlg.querySelector('[data-bb-count="gifts"]').textContent = `${gifts}/${BB_GIFTS}`;
  const status = dlg.querySelector('[data-bb-status]');
  const go = dlg.querySelector('[data-bb-go]');
  go.classList.remove('is-ready');
  if (hats < BB_HATS) {
    const left = BB_HATS - hats;
    status.textContent = `${hats} of ${BB_HATS} hats picked`;
    go.textContent = `Pick ${left} more hat${left > 1 ? 's' : ''}`;
  } else if (gifts < BB_GIFTS) {
    const left = BB_GIFTS - gifts;
    status.textContent = `Hats done · ${gifts} of ${BB_GIFTS} FREE accessories`;
    go.textContent = st.step === 'gifts' ? `Pick ${left} more FREE accessor${left > 1 ? 'ies' : 'y'}` : 'Next: pick 2 FREE accessories';
    if (st.step !== 'gifts') go.classList.add('is-ready');
  } else {
    status.textContent = '3 hats + 2 FREE accessories';
    go.textContent = `Add bundle to cart · ${dlg.querySelector('.td-bb__price')?.firstChild?.textContent.trim() || '$99'}`;
    go.classList.add('is-ready');
  }
}

function bbClick(dlg, st, e) {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  const stepBtn = t.closest('[data-bb-step]');
  if (stepBtn) return bbStep(dlg, st, stepBtn.dataset.bbStep), bbRender(dlg, st);
  const filter = t.closest('[data-bb-filter]');
  if (filter) {
    const f = filter.dataset.bbFilter;
    dlg.querySelectorAll('[data-bb-filter]').forEach((b) => b.classList.toggle('is-on', b === filter));
    dlg.querySelectorAll('[data-bb-hat]').forEach((c) => (c.hidden = f !== 'all' && !(c.dataset.cats || '').split(' ').includes(f)));
    return;
  }
  const hat = t.closest('[data-bb-hat]');
  if (hat) {
    const v = hat.dataset.v;
    const n = st.hats.get(v) || 0;
    const total = bbHatCount(st);
    if (t.closest('[data-bb-dec]')) {
      if (n <= 1) st.hats.delete(v);
      else st.hats.set(v, n - 1);
    } else if ((t.closest('[data-bb-add]') || t.closest('[data-bb-inc]') || (!n && t.closest('.td-bb__img'))) && total < BB_HATS) {
      st.hats.set(v, n + 1);
      if (total + 1 === BB_HATS && st.gifts.size < BB_GIFTS) {
        bbRender(dlg, st);
        setTimeout(() => bbStep(dlg, st, 'gifts'), 350);
      }
    }
    return bbRender(dlg, st);
  }
  const gift = t.closest('[data-bb-gift]');
  if (gift) {
    const v = gift.dataset.v;
    if (st.gifts.has(v)) st.gifts.delete(v);
    else {
      for (const [k, p] of st.gifts) if (p === gift.dataset.p) st.gifts.delete(k); // one per item: swap design
      if (st.gifts.size < BB_GIFTS) st.gifts.set(v, gift.dataset.p);
    }
    return bbRender(dlg, st);
  }
  if (t.closest('[data-bb-go]')) {
    const hats = bbHatCount(st);
    if (hats < BB_HATS) return bbStep(dlg, st, 'hats'), bbRender(dlg, st);
    if (st.gifts.size < BB_GIFTS) return bbStep(dlg, st, 'gifts'), bbRender(dlg, st);
    bbAdd(dlg, st);
  }
}

async function bbAdd(dlg, st) {
  const go = dlg.querySelector('[data-bb-go]');
  const err = dlg.querySelector('[data-bb-err]');
  if (go.classList.contains('is-loading')) return;
  go.classList.add('is-loading');
  err.hidden = true;
  const { offer, addon, giftTier, giftOffer, fallback } = dlg.dataset;
  try {
    go.textContent = 'Adding your bundle…';
    const before = await (st.cartBefore || fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()));
    const bundlesBefore = before.items.filter((i) => /mix and match bundle/i.test(i.product_title || i.title || '')).reduce((a, i) => a + i.quantity, 0);
    const boxes = before.items.map((i) => Number(i.properties?.Box || 0)).filter(Boolean);
    const box = String(boxes.length ? Math.max(...boxes) + 1 : 1);
    const items = [];
    let slot = 0;
    dlg.querySelectorAll('[data-bb-hat]').forEach((card) => {
      const n = st.hats.get(card.dataset.v) || 0;
      for (let k = 0; k < n; k++) {
        slot += 1;
        items.push({
          id: Number(card.dataset.v),
          quantity: 1,
          properties: { _Category: card.dataset.cat || 'category64802', Box: box, '_easyBundle:OfferId': `${offer}_JSR_${slot}$ADP:${addon}`, '_easyBundle:prodQty': '1', _addon_offer_id: addon },
        });
      }
    });
    for (const v of st.gifts.keys()) {
      items.push({ id: Number(v), quantity: 1, properties: { _Category: 'freeGiftProduct', _free_gift: 'true', _freeGiftTierId: giftTier, _free_gift_offer_id: giftOffer, Box: box } });
    }
    await addLines(dlg, items);
    const [after] = await Promise.all([
      fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()),
      import('@theme/section-renderer').then((m) => m.sectionRenderer.renderSection('cart-drawer-section', { cache: false })).catch(() => {}),
    ]);
    const bundlesAfter = after.items.filter((i) => /mix and match bundle/i.test(i.product_title || i.title || '')).reduce((a, i) => a + i.quantity, 0);
    if (bundlesAfter <= bundlesBefore) {
      // The bundle engine didn't price it. Take the loose items back out and use Easy Bundle's own page.
      const loose = after.items.filter((i) => i.properties && i.properties.Box === box);
      for (const line of loose) {
        await fetch(ROOT + 'cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: line.key, quantity: 0 }) });
      }
      window.location.href = fallback;
      return;
    }
    dlg.close();
    bbState.delete(dlg);
    const td = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
    if (td && !td.isOpen) td.open?.();
  } catch (e) {
    console.warn('[trhc-bundle]', e);
    err.textContent = 'Something went wrong adding your bundle. Try again, or use the full bundle page.';
    err.hidden = false;
  } finally {
    go.classList.remove('is-loading');
    if (bbState.has(dlg)) bbRender(dlg, st);
  }
}

document.addEventListener('click', (e) => {
  const opener = e.target instanceof Element ? e.target.closest('[data-td-bb-open]') : null;
  if (!opener) return;
  const dlg = document.getElementById(opener.dataset.tdBbOpen);
  if (!dlg || !dlg.showModal) return; // no pop-up: the link goes to the Easy Bundle page
  e.preventDefault();
  const st = bbInit(dlg);
  st.cartBefore = fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()); // ready before they finish picking
  dlg.showModal();
});

/* ---------- Cart: if a bundle is removed, take its FREE accessories out too ---------- */
async function cleanOrphanBundleGifts() {
  try {
    const c = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const boxes = new Set(c.items.filter((i) => /mix and match bundle/i.test(i.product_title || '')).map((i) => String(i.properties?.Box || '1')));
    const orphans = c.items.filter((i) => i.properties?._free_gift === 'true' && !boxes.has(String(i.properties?.Box || '1')));
    if (!orphans.length) return;
    const updates = {};
    orphans.forEach((o) => (updates[o.key] = 0));
    await fetch(ROOT + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) });
    refreshDrawer(false);
  } catch (e) {}
}
/* Self-heal: if the drawer ends up showing "Your cart is empty" while the cart actually has items (seen once on a
   phone after a second add), re-render it from the server. */
async function healEmptyDrawer() {
  try {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;
    const looksEmpty = drawer.querySelector('.cart-drawer--empty, [data-cart-drawer-empty]') || !drawer.querySelector('.cart-items__table-row');
    if (!looksEmpty) return;
    const c = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }).then((r) => r.json());
    if (c.item_count > 0) {
      console.warn('[trhc] drawer showed empty with', c.item_count, 'items; re-rendering');
      const m = await import('@theme/section-renderer');
      await m.sectionRenderer.renderSection('cart-drawer-section', { cache: false, mode: 'hydration', injectStylesheet: true });
    }
  } catch (e) {}
}
if (StandardEvents && StandardEvents.cartLinesUpdate) {
  document.addEventListener(StandardEvents.cartLinesUpdate, (e) => {
    const done = e.promise && typeof e.promise.then === 'function' ? e.promise : Promise.resolve();
    done.finally(() => setTimeout(healEmptyDrawer, 900)).catch(() => {});
  });
  document.addEventListener(StandardEvents.cartLinesUpdate, (e) => {
    if (e.action === 'add') return;
    const p = e.promise && typeof e.promise.then === 'function' ? e.promise : Promise.resolve();
    p.then(() => setTimeout(cleanOrphanBundleGifts, 250)).catch(() => {});
  });
}


/* ---------- Back in stock: arriving from a sold-out card (?notify=1) opens Postscript's sign-up ----------
   Postscript's script injects its link into #ps__bis_target_container (snippets: trhc-pdp "trust") on sold-out hats.
   We wait for it, scroll to it and tap it for the shopper. If it never appears, we just scroll to the buy box. */
(function notifyFromCard() {
  let params;
  try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
  if (!params.has('notify')) return;
  const RX = /back in stock|notify me|text me|restock|let me know|get notified/i;
  try {
    params.delete('notify');
    const qs = params.toString();
    history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
  } catch (e) {}
  const find = () => {
    const ps = document.querySelector('#ps-bis-widget__button, .ps-bis-button');
    if (ps) return ps;
    const box = document.getElementById('ps__bis_target_container');
    const inBox = box && box.querySelector('a, button, [role="button"]');
    if (inBox) return inBox;
    return Array.from(document.querySelectorAll('.buy-buttons-block a, .buy-buttons-block button, product-form-component a, product-form-component button'))
      .find((el) => !el.disabled && RX.test(el.textContent || ''));
  };
  let tries = 0;
  const timer = setInterval(() => {
    tries += 1;
    const el = find();
    if (el) {
      clearInterval(timer);
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setTimeout(() => el.click(), 450);
    } else if (tries > 60) {
      clearInterval(timer);
      const buy = document.querySelector('.buy-buttons-block');
      if (buy) buy.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, 250);
})();
